import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import TurnstileWidget from '../TurnstileWidget';
import { Input } from '../ui/input';
import { FormControl, FormField, FormItem, FormMessage } from '../ui/form';
import { getAttribution, newSubmissionId, trackEvent } from '../../lib/attribution';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useContactSubmit = (form, { buildPayload, successMessage, formName }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const submissionId = useRef(newSubmissionId());

  const onSubmit = async (data) => {
    if (!turnstileToken) {
      toast.error('Please complete the CAPTCHA verification.');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        ...buildPayload(data),
        city: data.city,
        zip: data.zip,
        consent: !!data.consent,
        website: data.website,
        turnstile_token: turnstileToken,
        submission_id: submissionId.current,
        form_name: formName,
        page_path: window.location.pathname,
        attribution: getAttribution(),
      });
      trackEvent('generate_lead', { form_name: formName, page_path: window.location.pathname });
      toast.success(successMessage);
      form.reset();
      setTurnstileToken('');
      submissionId.current = newSubmissionId();
    } catch (error) {
      if (String(error.response?.data?.detail || '').includes('CAPTCHA')) {
        toast.error('CAPTCHA verification failed. Please try again.');
      } else {
        toast.error('Something went wrong. Please try again or call us directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return { onSubmit, isSubmitting, turnstileToken, setTurnstileToken };
};

export const HoneypotField = ({ form }) => (
  <div className="absolute -left-[9999px]" aria-hidden="true">
    <input type="text" {...form.register('website')} tabIndex={-1} autoComplete="off" />
  </div>
);

export const locationSchemaFields = (z) => ({
  city: z.string().trim().min(2, 'Please enter your city'),
  zip: z.string().trim().regex(/^\d{5}(-\d{4})?$/, 'Enter a 5-digit ZIP').or(z.literal('')).optional(),
});

export const CityZipFields = ({ form, prefix, inputClassName, showLabels = false, labelClassName = '' }) => (
  <>
    {[['city', 'City', 'address-level2'], ['zip', 'ZIP Code', 'postal-code']].map(([name, label, ac]) => (
      <FormField
        key={name}
        control={form.control}
        name={name}
        render={({ field }) => (
          <FormItem>
            {showLabels && <label htmlFor={`${prefix}-${name}`} className={labelClassName}>{label}{name === 'zip' ? ' (optional)' : ''}</label>}
            <FormControl>
              <Input
                id={`${prefix}-${name}`}
                placeholder={showLabels ? (name === 'city' ? 'e.g. Saint Paul' : 'e.g. 55101') : (name === 'city' ? 'City' : 'ZIP Code (optional)')}
                autoComplete={ac}
                inputMode={name === 'zip' ? 'numeric' : undefined}
                {...field}
                className={inputClassName}
                data-testid={`${prefix}-${name}`}
              />
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>
        )}
      />
    ))}
  </>
);

export const CaptchaField = ({ setToken, className = 'flex justify-start' }) => (
  <div className={className}>
    <TurnstileWidget
      onVerify={(token) => setToken(token)}
      onError={() => toast.error('CAPTCHA failed to load. Please refresh the page.')}
      onExpire={() => setToken('')}
    />
  </div>
);

export const SmsConsentField = ({ form, testId, textClassName = 'text-sm' }) => (
  <FormField
    control={form.control}
    name="consent"
    render={({ field }) => (
      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
        <FormControl>
          <input
            type="checkbox"
            id={testId}
            checked={field.value}
            onChange={(e) => field.onChange(e.target.checked)}
            data-testid={testId}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-[#F58220] focus:ring-[#F58220]"
          />
        </FormControl>
        <label htmlFor={testId} className={`${textClassName} text-gray-600 font-normal cursor-pointer leading-snug`}>
          By checking this box and signing up for texts, you agree to receive informational messages (appointment reminders, account notifications, marketing and promotional messages such as sales, coupons, seasonal deals, etc.) from Bruce Nelson Plumbing & Heating at the number provided. Msg & data rates may apply. Msg frequency varies. Unsubscribe anytime by replying STOP. Reply HELP for help or email us at{' '}
          <a href="mailto:info@brucenelsonph.com" className="text-[#F58220] hover:underline">info@brucenelsonph.com</a>. Learn more on our{' '}
          <Link to="/privacy-policy" className="text-[#F58220] hover:underline" target="_blank">Privacy Policy</Link>
          {' '}and{' '}
          <Link to="/terms-conditions" className="text-[#F58220] hover:underline" target="_blank">Terms & Conditions</Link>
          {' '}page.
        </label>
      </FormItem>
    )}
  />
);
